import React, { useMemo, useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_WORK_ORDER } from '../../graphql/mutations';
import { BULK_UPDATE_CUSTOM_WORK_ORDER_FLAGS } from '../../graphql/work-order-flags';
import WorkOrderForm from './WorkOrderForm';

export default function CreateWorkOrderModal({ isOpen, onClose, propertyId, propertyName, reservationId = null }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const formRef = useRef(null);

  const [createWorkOrder, { loading }] = useMutation(CREATE_WORK_ORDER, {
    onError: (error) => {
      console.error('Error creating work order:', error);
      // Check for specific error messages
      if (error.message?.includes('property')) {
        setErrorMessage('Error with property selection. Please select a valid property and try again.');
      } else if (error.message?.includes('reservation')) {
        setErrorMessage('Error with reservation. Please try again without a reservation.');
      } else {
        setErrorMessage(error.message || 'An error occurred while creating the work order. Please try again.');
      }
    },
    fetchPolicy: 'no-cache'
  });
  const [bulkUpdateFlags] = useMutation(BULK_UPDATE_CUSTOM_WORK_ORDER_FLAGS);

  const handleSubmit = async (formData) => {
    setErrorMessage(''); // Clear previous error
    
    try {
      console.log('Form data received:', formData);
      
      // Validate required fields
      if (!formData.propertyId || !formData.description?.trim()) {
        setErrorMessage('Please fill in all required fields');
        return false;
      }
      
      // Create work order data
      const workOrderData = {
        propertyId: formData.propertyId,
        description: formData.description.trim(),
        isUrgent: !!formData.isUrgent,
        isAfterHours: !!formData.isAfterHours,
        isFeedback: !!formData.isFeedback,
        workOrderType: formData.workOrderType,
      };
      
      // Add optional fields
      if (formData.photos && formData.photos.length > 0) {
        workOrderData.photos = formData.photos;
      }
      
      if (formData.visibilityRoles && formData.visibilityRoles.length > 0) {
        workOrderData.visibilityRoles = formData.visibilityRoles;
      }

      if (formData.reservationId || reservationId) {
        workOrderData.reservationId = formData.reservationId || reservationId;
      }

      workOrderData.guestAlertsEnabled = !!formData.guestAlertsEnabled;
      if (formData.guestAlertsEnabled) {
        if (formData.guestAlertDescription?.trim()) {
          workOrderData.guestAlertDescription = formData.guestAlertDescription.trim();
        }
        if (formData.guestAlertUseContactOverride && formData.guestAlertEmailOverride?.trim()) {
          workOrderData.guestAlertEmailOverride = formData.guestAlertEmailOverride.trim();
        }
        if (formData.guestAlertUseContactOverride && formData.guestAlertPhoneOverride?.trim()) {
          workOrderData.guestAlertPhoneOverride = formData.guestAlertPhoneOverride.trim();
        }
      }

      // Add assignment data if provided
      if (formData.assignedTeamId) {
        workOrderData.assignedTeamId = parseInt(formData.assignedTeamId, 10);
      } else if (formData.assignedToId) {
        workOrderData.assignedToId = parseInt(formData.assignedToId, 10);
      }

      if (formData.assignedRoles && formData.assignedRoles.length > 0) {
        workOrderData.assignedRoles = formData.assignedRoles;
      }

      // Add due date if provided
      if (formData.dueDate) {
        // Parse the date string and create a Date object using local time components
        const [year, month, day] = formData.dueDate.split('-').map(Number);
        workOrderData.dueDate = new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid timezone edge cases
        console.log('Adding dueDate to work order:', workOrderData.dueDate);
      }
      
      console.log('Submitting work order:', workOrderData);
      
      const result = await createWorkOrder({
        variables: {
          input: workOrderData
        }
      });

      const workOrderId = result?.data?.createWorkOrder?.id;
      if (workOrderId && formData.workOrderFlagTemplateIds?.length > 0) {
        await bulkUpdateFlags({
          variables: {
            input: {
              workOrderId,
              flags: formData.workOrderFlagTemplateIds.map((templateId, index) => ({
                workOrderId,
                templateId,
                flagType: 'custom',
                flagValue: 'template',
                sortOrder: index
              }))
            }
          }
        });
      }

      console.log('Work order created successfully:', result?.data);
      onClose(true);
      return true;
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      return false;
    }
  };
  
  const handleModalSubmit = () => {
    if (isPhotoUploading) return;
    if (formRef.current && formRef.current.submitForm) {
      void formRef.current.submitForm();
    }
  };

  const initialData = useMemo(() => ({
    propertyId,
    reservationId,
    property: {
      id: propertyId,
      name: propertyName,
    },
    propertySearchTerm: propertyName || '',
  }), [propertyId, propertyName, reservationId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Create Work Order{propertyName ? ` for ${propertyName}` : ''}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    Create a work order to report an issue with this property.
                  </p>
                  
                  {errorMessage && (
                    <div className="bg-red-50 p-4 rounded-md mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Error</h3>
                          <div className="mt-1 text-sm text-red-700">
                            <p>{errorMessage}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <WorkOrderForm 
                    ref={formRef}
                    initialData={initialData}
                    onSubmit={handleSubmit} 
                    isLoading={loading} 
                    hideButtons={true}
                    showAssignment={true}
                    onUploadStateChange={setIsPhotoUploading}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleModalSubmit}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              disabled={loading || isPhotoUploading}
            >
              {isPhotoUploading ? 'Finishing Photo…' : loading ? 'Submitting...' : 'Submit Work Order'}
            </button>
            <button
              type="button"
              onClick={() => onClose(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
